import { useCallback } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router-dom'

import { Heading, Text } from '@buffer-mono/popcorn'

import { TemplateCard } from '~publish/components/TemplateCard'

import type { Template, TemplateCampaign } from '../../types'
import { getCampaignTagsForTemplate } from '../utils'

import styles from './FeaturedTemplatesBanner.module.css'

export const FeaturedTemplatesBanner = ({
  templates,
  activeCampaigns = [],
}: {
  templates: Template[]
  activeCampaigns?: TemplateCampaign[]
}): JSX.Element | null => {
  const getTemplateCampaignTags = useCallback(
    (template: Template) => {
      return getCampaignTagsForTemplate({
        template,
        activeCampaigns,
      })
    },
    [activeCampaigns],
  )

  if (templates.length === 0) return null

  return (
    <div className={styles.container}>
      <aside className={styles.aside}>
        <Text color="brand" weight="medium">
          Featured templates
        </Text>
        <Heading as="h2" size="xl">
          Our top picks
        </Heading>
        <Text>Helpful starting points to plan your next post.</Text>
      </aside>
      <ul className={styles.list}>
        {templates.map((template) => (
          <li key={template.id}>
            <TemplateCard template={template}>
              <TemplateCard.Clickable
                asChild
                id={`template-card-featuredBanner-${template.id}`}
              >
                <Link
                  to={{
                    pathname: `/create/templates/template/${template.id}`,
                    search: `cta=create-templates-featuredBanner-template-1&trigger=template-card-featured-${template.id}`,
                  }}
                  aria-label={`Open template ${template.title}`}
                />
              </TemplateCard.Clickable>
              <TemplateCard.Icon />
              <TemplateCard.Title />
              <TemplateCard.Description />
              <TemplateCard.Tags
                customTags={getTemplateCampaignTags(template)}
              />
            </TemplateCard>
          </li>
        ))}
      </ul>
    </div>
  )
}
